// "Are you sure?" before saving a hand-typed reading that sits far outside
// the keeper's own range (owner, 2026-09-28). A typo (0.84 for 8.4, a
// reading on the wrong card) is far more common than a real crash, and a
// wrong number here sends a severe alert and a dose plan. One extra tap
// for a real crash is the cheap side of that trade.
//
// Not a judgement of the tank: the range is the keeper's own band, and
// the question is only "is this the number you meant". Values no kit can
// read at all are refused outright elsewhere (reading-plausibility.js).
// Pure, storage units (alk dKH, ppm, salinity ppt, temp °F).

import { isGradedParam } from './tank-purpose.js';

// How far outside the band a value must sit before we ask, per param, so
// a tight band (salinity 34.7-35.3) does not ask on every ordinary
// wobble. The step is the larger of this and the band's own width.
const MIN_STEP = {
  alk: 1.5,
  calcium: 50,
  magnesium: 150,
  salinity: 3,
  temp: 4,
  ph: 0.4,
  nitrate: 10,
  phosphate: 0.1,
  kh: 2,
  gh: 3,
};

// Zero-target params (ammonia, nitrite) are left alone by key as well as
// by band: a cycling tank or a QT reads them high for real, day after
// day, and asking each time would teach the keeper to tap through it.
const NEVER_ASK = new Set(['ammonia', 'nitrite']);

// A new tank's chemistry swings on purpose, so nothing is asked while it
// is set up or cycling.
const QUIET_PHASES = new Set(['setup', 'cycling']);

// Params a phase logs without grading. Mirrors DEFAULT_PHASE_OVERRIDES
// track_only in server/db/seeds (test/reading-confirm.test.js pins it):
// shared code cannot import the server seeds.
export const PHASE_LOGGED = {
  cycling: ['alk', 'calcium', 'magnesium'],
  maturing: ['calcium', 'magnesium'],
  growing: [],
  established: [],
};

// null, or { value, band: [lo, hi], direction } for the client to word in
// the keeper's own units. `previous` is the last stored value for the
// param: when it is already that far out on the same side, the keeper has
// seen it and is tracking it, so asking again on every test would nag.
export function readingNeedsConfirm(tank, param, value, band, { previous = null } = {}) {
  const v = Number(value);
  if (!Number.isFinite(v) || !Array.isArray(band) || band.length !== 2) return null;
  if (NEVER_ASK.has(param)) return null;
  if (QUIET_PHASES.has(tank?.phase)) return null;
  if ((PHASE_LOGGED[tank?.phase] || []).includes(param)) return null;
  // A QT's logged-only params move on purpose (hyposalinity).
  if (!isGradedParam(tank, param)) return null;
  const lo = Number(band[0]);
  const hi = Number(band[1]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (lo === hi) return null;
  const step = Math.max(hi - lo, MIN_STEP[param] ?? 0);
  if (step <= 0) return null;
  const prev = previous == null ? NaN : Number(previous);
  if (v < lo - step) {
    if (prev < lo - step) return null;
    return { value: v, band: [lo, hi], direction: 'low' };
  }
  if (v > hi + step) {
    if (prev > hi + step) return null;
    return { value: v, band: [lo, hi], direction: 'high' };
  }
  return null;
}
